/** @jsx jsx */
import { Grid, Icon, Modal, Spacer, TextGroupMedium } from "@cd2/components"
import { Fields, Form2 } from "@cd2/forms"
import { H3, P } from "@cd2/text/build/es6"
import { useTheme } from "@cd2/theme"
import { assert, IComponentCallback, IComponentProp } from "@cd2/tools/build/es6"
import { css, jsx } from "@emotion/core"
import React, { useEffect } from "react"
import { ConfirmCancelButtons } from "../../Components/Buttons/ConfirmCancelButtons"
import { CreateButton } from "../../Components/Buttons/CreateButton"
import { Button } from "../../Components/NewDesign/Button"
import { brandColors, themeColors } from "../../Components/EOSThemeProvider"
import { FieldsRadioButtonSelect, FieldsRadioCheckbox } from "../../Components/FieldsRadioCheckbox"
import { CurrencySelect } from "../../Components/FormFields/CurrencySelect"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { FloatingBox } from "../../Components/Misc/FloatingBox"
import { ContactSelectList } from "../../Components/SelectLists/ContactSelectList"
import { LeadsSelect } from "../../Components/SelectLists/LeadsSelectList"
import { SubCustomerSelectList } from "../../Components/SelectLists/SubCustomerSelectField"
import { ToggleSwitch } from "../../Components/ToggleSwitch"
import { useContactCreateForSubOrCustomerConfirmation } from "../../Pages/SubCustomers/SubCustomerShowContactsPage"
import { icons } from "../../constants/icons"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useConfirmation } from "../../hooks/useConfirmation"
import { useCustomerMemo } from "../../hooks/useCustomerMemo"
import { useNewLead } from "../../hooks/useNewLead"
import { useReloadKey } from "../../hooks/useReloadKey"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { HoverCustomerLoader } from "../../hoverLoaders/HoverCustomerLoader"
import { IRecord } from "../../types/schema"
import { currencyNameToSymbol, getCurrencyOption } from "../../utils/currency"
import {
  isSplitPaymentMethod,
  joinPaymentMethods,
  PAYMENT_METHOD_NAMES,
  paymentAmountField,
  paymentMethodParts,
  PaymentMethodName,
} from "../../utils/paymentDetails"
import { useSettings } from "../../utils/useSettings"
import {
  getOrderTotalPennies,
  IAddress,
  ICustomerHeadingFieldsProps,
  IQuoteOrderRecord,
} from "./quoteOrderUtils"
import { CustomerTagFilter } from "../../Components/FormFields/CustomerTagFilter"
import { BranchSelect } from "../../Components/SelectLists/BranchSelect"

interface ICustomerPanelProps {
  quote: IQuoteOrderRecord
  errors: any
  CustomerHeadingFields: (props: ICustomerHeadingFieldsProps) => JSX.Element
  isQuote: boolean
  isNewPage?: boolean
  disableCustomerSelect?: boolean

  onChange(quote: Partial<IQuoteOrderRecord & { customer: ICustomer }>): void
  onChangeDpdProduct(data?: {
    dpdProduct: IQuoteOrderRecord["dpdProduct"]
    carriage_pennies: IQuoteOrderRecord["carriage_pennies"]
  }): void
}

type IBranchRef = IRecord<"branches", "name">
type IOrderRequestBranchPairing = IRecord<"orderRequestBranches"> & {
  sourceBranch?: IBranchRef
  targetBranch?: IBranchRef
}
type IInternalTransferTarget = { branch: IBranchRef }

/**
 * An orderRequestBranch pairs the branch that requests stock (sourceBranch) with the one that
 * supplies it (targetBranch), and its customer is how the requester appears on the supplier's
 * books - the admin form scopes that customer picker to the targetBranch. So an order raised here
 * against one of our internal customers is us supplying, and the goods transfer to its
 * sourceBranch.
 */
async function fetchInternalTransferTarget(
  customerId: string,
  { branchId }: { branchId: string | null },
): Promise<IInternalTransferTarget | undefined> {
  if (!branchId) {
    return undefined
  }
  // Read the pairings off the customer rather than filtering orderRequestBranches by it, so this
  // can only ever see rows belonging to the customer they picked.
  const customer = await window.skeem.fetch2<{
    orderRequestBranches: IOrderRequestBranchPairing[]
  } | null>({
    first: {
      name: "customers",
      matches: { id: customerId },
      attributes: [
        {
          name: "orderRequestBranches",
          attributes: [
            { name: "sourceBranch", attributes: ["name"] },
            { name: "targetBranch", attributes: ["name"] },
          ],
        },
      ],
    },
  })

  const pairings = (customer?.orderRequestBranches ?? []).filter(
    row =>
      row.targetBranch?.id === branchId && row.sourceBranch && row.sourceBranch.id !== branchId,
  )
  const candidates = pairings

  // One customer can be wired to several pairings, and then only whoever set it up knows which
  // branch was meant. Leave it blank to be filled in by hand rather than guessing.
  const branchIds = new Set(candidates.map(row => row.sourceBranch!.id))
  if (branchIds.size !== 1) {
    return undefined
  }
  const [match] = candidates
  return { branch: match.sourceBranch! }
}

export function CustomerPanel({
  quote,
  onChange,
  onChangeDpdProduct,
  errors,
  CustomerHeadingFields,
  isQuote,
  isNewPage,
  disableCustomerSelect,
}: ICustomerPanelProps) {
  const [reloadLeadKey, reloadLead] = useReloadKey()
  const [useLead, setUseLead] = React.useState(!!quote.lead && !quote.customer)
  const [showPaymentDetails, setShowPaymentDetails] = React.useState(false)
  const { currentUser } = useGlobalState()
  const skeem = window.skeem
  const settings = useSettings()
  const theme = useTheme()
  const { customerMemoNode, alertCustomerMemos } = useCustomerMemo()
  const [contactFormNode, _getContactInformation, handleCreateContactFromNode] =
    useContactCreateForSubOrCustomerConfirmation()
  const [newLeadNode, handleCreateLead] = useNewLead()
  const [createSubCustomerNode, handleCreateSubCustomer] = useCreateSubCustomer()
  const [contactSelectKey, reloadContactSelect] = useReloadKey()
  const [subCustomerSelectKey, reloadSubCustomerSelect] = useReloadKey()
  const alerts = useSiteAlerts()

  const transfersEnabled = !isQuote && settings.allowInternalOrderTransfers
  const [transferBranchLookup, setTransferBranchLookup] = React.useState<{
    customerId: string
    target?: IInternalTransferTarget
  }>()

  // Only a customer we have actually paired with a branch settles the question of whether this is
  // an internal transfer. Without one there is nothing to fill in, so the toggle is theirs.
  const customerTransferBranch =
    transferBranchLookup?.customerId === quote.customer?.id
      ? transferBranchLookup?.target
      : undefined
  const customerForcesInternalTransfer = transfersEnabled && !!customerTransferBranch

  async function lookupTransferBranch(customerId: string) {
    const target = await fetchInternalTransferTarget(customerId, {
      branchId: currentUser.branch.id,
    })
    setTransferBranchLookup({ customerId, target })
    return target
  }

  // Resolve the customer an order arrives with too, so editing one locks the toggle the same way
  // picking the customer does. This only sets local state - changing the record from here would
  // mark the form dirty before they have touched it.
  useAsyncEffect(async () => {
    const customerId = quote.customer?.id
    if (
      !transfersEnabled ||
      !customerId ||
      !quote.customer?.internalCustomer ||
      transferBranchLookup?.customerId === customerId
    ) {
      return
    }
    try {
      await lookupTransferBranch(customerId)
    } catch (e) {
      // Nothing to tell them about yet - a failed lookup just leaves the toggle as they left it.
    }
  }, [quote.customer?.id])

  async function handleCustomerSelectChange(
    data: IComponentCallback<typeof CustomerSelect, "onChange">,
  ) {
    if (!transfersEnabled || !data.customer?.internalCustomer) {
      return onChange(data as any)
    }
    let transferTo: IInternalTransferTarget | undefined
    try {
      transferTo = await lookupTransferBranch(data.customer.id)
    } catch (e) {
      // Don't lose the customer they just picked over a failed lookup.
      alerts.genericError("Could not look up the transfer branch for this internal customer")
    }
    if (!transferTo) {
      // No branch paired with this customer, so leave the transfer fields alone for them to set.
      return onChange(data as any)
    }
    return onChange({
      ...data,
      isInternalTransfer: true,
      // Overwrite both, so swapping between paired customers can't leave the previous customer's
      // branch behind.
      internal_transfer_to_branch: transferTo.branch,
    } as any)
  }

  async function handleNewSubCustomer() {
    if (!quote.customer) {
      return
    }
    const subCustomer = await handleCreateSubCustomer({ customer: quote.customer })
    if (subCustomer) {
      onChange({ sub_customer: subCustomer })
      reloadSubCustomerSelect()
    }
  }

  async function handleCreateContact() {
    assert(quote.customer)
    const customerRecord = await skeem.fetch2<
      IRecord<"customers", "name" | "isGenericCustomer"> & { branch: IRecord<"branches"> }
    >({
      first: {
        name: "customers",
        attributes: ["name", "branch", "isGenericCustomer"],
        filter: { matches: { id: quote.customer.id } },
      },
    })
    let subCustomerRecord: IRecord<"sub_customers", "name"> | undefined
    if (quote.sub_customer?.id) {
      subCustomerRecord = await skeem.fetch2<IRecord<"sub_customers", "name">>({
        first: {
          name: "sub_customers",
          attributes: ["name"],
          filter: { matches: { id: quote.sub_customer.id } },
        },
      })
    }
    try {
      const contact = await handleCreateContactFromNode(customerRecord, subCustomerRecord)
      if (contact) {
        if (!isQuote && contact.default_delivery_note) {
          onChange({
            contact: { id: contact.id, email: contact.email },
            delivery_note_email: contact.email,
          })
        } else {
          onChange({ contact: { id: contact.id, email: contact.email } })
        }

        reloadContactSelect()
      }
    } catch (e) {
      alerts.genericError(JSON.stringify(e))
    }
  }

  async function handleNewLead() {
    const me = await skeem.fetchMe<{ branch: IRecord<"branches", "name"> }>([
      { name: "branch", attributes: ["name"] },
    ])
    const lead = await handleCreateLead({ branch: me.branch })
    if (lead) {
      onChange({ lead: lead })
      reloadLead()
    }
  }

  useEffect(() => {
    if (quote.customer && isNewPage) {
      alertCustomerMemos(quote.customer.id, { onlyOfType: "alerts" })
    }
  }, [quote.customer?.id])

  return (
    <Grid
      columns={[400, { fr: 1 }]}
      gap={16}
      css={css`
        max-width: 800px;
      `}
    >
      {newLeadNode}
      {customerMemoNode}
      {contactFormNode}
      {createSubCustomerNode}
      <FloatingBox
        color="#f5f5f5"
        css={css`
          z-index: 2;
          position: relative;
          max-width: 500px;
          background-color: #f5f5f5;
          border-radius: ${theme.borderRadius}px;
        `}
      >
        {transfersEnabled && (
          <React.Fragment>
            <ToggleSwitch
              value={quote.isInternalTransfer}
              onChange={value => {
                const updateData = { isInternalTransfer: value } as any
                if (!value) {
                  updateData.internal_transfer_to_branch = null
                }
                return onChange(updateData)
              }}
              label="Order is for internal transfer"
              disabled={
                disableCustomerSelect ||
                (customerForcesInternalTransfer && quote.isInternalTransfer)
              }
            />
            <Spacer />
            {quote.isInternalTransfer && (
              <React.Fragment>
                <BranchSelect
                  label="Choose branch"
                  onChange={branch => onChange({ internal_transfer_to_branch: branch })}
                  value={quote.internal_transfer_to_branch ?? undefined}
                  error={errors.internal_transfer_to_branch}
                />
                <Spacer />
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        <Grid gap={16}>
          {!useLead && (
            <CustomerSelect
              quote={quote}
              customer={quote.customer}
              error={errors.customer}
              onChange={handleCustomerSelectChange}
              isQuote={isQuote}
              disableOnHoldCustomers={!settings.enableOnHoldOrders}
              isClearable={isQuote}
              disabled={disableCustomerSelect}
              postFieldText={
                !quote.customer &&
                isQuote && (
                  <React.Fragment>
                    <div
                      onClick={() => setUseLead(true)}
                      css={css`
                        cursor: pointer;
                        border-bottom: 1px dotted black;
                        width: max-content;
                      `}
                    >
                      <P blockSpacing={0} size="small">
                        Use a lead instead of customer
                      </P>
                    </div>
                  </React.Fragment>
                )
              }
            />
          )}
          {quote.customer?.id && (
            <div>
              <SubCustomerSelectList
                key={subCustomerSelectKey}
                value={quote.sub_customer?.id}
                branchId={currentUser.branch?.id ?? undefined}
                customerId={quote.customer?.id ?? undefined}
                onChange={(_, record) => {
                  onChange({ sub_customer: record })
                  reloadContactSelect()
                }}
                disabled={!quote.customer}
                error={errors.sub_customer}
                noBranchFilter={!!quote.customer}
                required
                isClearable
                hideIfNone
              />
              <div>
                <CreateButton
                  onClick={() => handleNewSubCustomer()}
                  buttonStyle="text"
                  extendTheme={{ padding: 8, fontSize: 12 }}
                  color="#666"
                >
                  Create new Sub Customer
                </CreateButton>
              </div>
            </div>
          )}
          {quote.customer?.id && (
            <div>
              <ContactSelectList
                key={contactSelectKey}
                value={quote.contact?.id}
                customerId={quote.customer?.id}
                subCustomerId={quote.sub_customer?.id}
                onChange={(_, contact) => onChange({ contact: contact })}
                disabled={!quote.customer}
                skeemFilter={{ not: { matches: { email: "" } } }}
                skipBranchFilter={disableCustomerSelect}
              />
              <div>
                <CreateButton
                  onClick={handleCreateContact}
                  buttonStyle="text"
                  extendTheme={{ padding: 8, fontSize: 12 }}
                  color="#666"
                >
                  Create new contact
                </CreateButton>
              </div>
            </div>
          )}
          {isQuote && useLead && (
            <LeadsSelect
              key={reloadLeadKey}
              label="Select Lead"
              onChange={(_, lead) => onChange({ lead: lead })}
              value={quote.lead?.id}
              branchId={currentUser.branch?.id ?? undefined}
              required={true}
              error={errors.lead}
              postFieldText={
                <React.Fragment>
                  <div
                    css={css`
                      padding-bottom: 4px;
                    `}
                  >
                    <CreateButton
                      onClick={handleNewLead}
                      buttonStyle="text"
                      extendTheme={{ padding: 8, fontSize: 12 }}
                      color="#666"
                    >
                      Create new lead
                    </CreateButton>
                  </div>
                  <div
                    onClick={() => setUseLead(false)}
                    css={css`
                      cursor: pointer;
                      border-bottom: 1px dotted black;
                      width: max-content;
                    `}
                  >
                    <P blockSpacing={0} size="small">
                      Assign to customer
                    </P>
                  </div>
                </React.Fragment>
              }
              isClearable
            />
          )}
          <CustomerHeadingFields
            quote={quote}
            onChange={onChange}
            onChangeDpdProduct={onChangeDpdProduct}
            errors={errors}
          />
          {settings.allowChangingQuoteOrderCurrency && (
            <CurrencySelect
              label="Currency"
              onChange={value => onChange({ currency_name: value })}
              value={quote.currency_name}
              error={errors.currency_name}
              required
            />
          )}
        </Grid>
      </FloatingBox>
      <div>
        {quote.customer?.id && (
          <React.Fragment>
            <HoverCustomerLoader id={quote.customer?.id} inline />
            {/* a split payment keeps its box even with the setting off, so its amounts can be corrected */}
            {!isQuote &&
              (settings.useOrderPaymentMethod || isSplitPaymentMethod(quote.paymentMethod)) && (
                <React.Fragment>
                  {quote.customer.cash_sales ||
                  quote.paymentMethod ||
                  showPaymentDetails ? (
                    <React.Fragment>
                      <Spacer />
                      <PaymentDetailsForm
                        quote={quote}
                        onChange={onChange}
                        errors={errors}
                        locations={settings.paymentLocations ?? []}
                        canSplit
                      />
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <Spacer />
                      <div>
                        <Button
                          icon={icons.plus}
                          label="Add payment details"
                          onClick={() => setShowPaymentDetails(true)}
                        />
                      </div>
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}
          </React.Fragment>
        )}
      </div>
    </Grid>
  )
}

export enum IPaymentMethod {
  BACS = "BACs",
  CASH = "Cash",
  CARD = "Card",
  SPLIT = "Cash & Card",
}
function PaymentDetailsForm({
  onChange,
  quote,
  errors,
  locations,
  canSplit,
}: {
  onChange: (data: any) => void
  quote?: IQuoteOrderRecord
  errors?: Record<string, any>
  locations: string[]
  canSplit: boolean
}) {
  const theme = useTheme()
  const { currentUser } = useGlobalState()
  const selectedMethods = paymentMethodParts(quote?.paymentMethod)
  const isSplit = selectedMethods.length > 1
  const currencySymbol =
    currencyNameToSymbol(quote?.currency_name || currentUser.branch.currency_name) || "£"
  const orderTotalPennies = quote ? getOrderTotalPennies(quote) : 0

  // whoever picks (or changes) the payment type is recorded as having taken the payment
  function selectMethods(methods: PaymentMethodName[]) {
    const paymentMethod = joinPaymentMethods(methods)
    if (quote?.paymentMethod === paymentMethod) {
      return
    }
    onChange({
      paymentMethod,
      paymentBacsPennies: undefined,
      paymentCashPennies: undefined,
      paymentCardPennies: undefined,
      paymentTakenBy: { id: currentUser.id, name: currentUser.name },
    })
  }

  // any combination of methods can be ticked to split the payment between them. The last ticked
  // method can't be unticked, only replaced.
  function toggleMethod(method: PaymentMethodName, checked: boolean) {
    if (checked) {
      selectMethods(canSplit ? [...selectedMethods, method] : [method])
    } else if (selectedMethods.length > 1) {
      selectMethods(selectedMethods.filter(selected => selected !== method))
    }
  }

  // with two methods, changing one amount fills the other with what is left of the order total
  function changeSplitAmount(method: PaymentMethodName, pennies?: number | null) {
    const entered = pennies ?? undefined
    const update: Record<string, number | undefined> = { [paymentAmountField(method)]: entered }
    if (selectedMethods.length === 2) {
      const other = selectedMethods.find(selected => selected !== method)!
      update[paymentAmountField(other)] =
        entered === undefined ? undefined : Math.max(orderTotalPennies - Math.round(entered), 0)
    }
    onChange(update)
  }

  return (
    <FloatingBox
      header={
        <H3 size="small" blockSpacing={0} color={brandColors.emerald}>
          Payment Details
        </H3>
      }
      color="white"
      css={css`
        z-index: 2;
        position: relative;
        max-width: 500px;
        background-color: white;
        border-radius: ${theme.borderRadius}px;
        width: 100%;
        min-width: 298px;
      `}
    >
      <div>
        {PAYMENT_METHOD_NAMES.map(method => (
          <FieldsRadioCheckbox
            key={`payment_${method}`}
            type="checkbox"
            label={method}
            value={selectedMethods.includes(method)}
            onChange={checked => toggleMethod(method, checked)}
            name={`payment_${method}`}
          />
        ))}
        {canSplit && !isSplit && (
          <P size="small" blockSpacing={0}>
            Tick more than one to split the payment.
          </P>
        )}
        {isSplit && (
          <React.Fragment>
            <Spacer />
            <Grid gap={8}>
              {selectedMethods.map(method => (
                <Fields.Price
                  // remounted when the methods change, as the amounts are cleared then
                  key={`${method}-${quote?.paymentMethod}`}
                  label={method}
                  value={quote?.[paymentAmountField(method)] ?? null}
                  onChange={value => changeSplitAmount(method, value)}
                  prefix={currencySymbol}
                  inputProps={{ min: 0 }}
                  inPennies
                />
              ))}
              <P size="small" blockSpacing={0} color={errors?.paymentSplit ? "error" : undefined}>
                {errors?.paymentSplit ??
                  `The amounts must add up to the order total of ${currencySymbol}${(
                    orderTotalPennies / 100
                  ).toFixed(2)}`}
              </P>
            </Grid>
          </React.Fragment>
        )}
        {locations.length > 0 && (
          <React.Fragment>
            <Spacer />
            <FieldsRadioButtonSelect
              label="Payment Location (required)"
              name="payment_location"
              options={locations.map(location => ({ label: location, value: location }))}
              value={quote?.paymentLocation}
              onChange={value => onChange({ paymentLocation: value })}
              error={errors?.paymentLocation}
            />
          </React.Fragment>
        )}
        <Spacer />
        <Fields.Date2
          label="Payment Date"
          // saved as today when left unset (see orderTapMutationBuilding)
          value={quote?.paymentDate ? new Date(quote.paymentDate) : new Date()}
          onChange={date => onChange({ paymentDate: date ? date.toISOString() : undefined })}
          dateFormat="dd/MM/yyyy"
        />
        <Spacer />
        <Fields.Text
          placeholder="Auth code"
          onChange={str => onChange({ paymentMethodNotes: str })}
          value={quote?.paymentMethodNotes ?? ""}
        />
      </div>
    </FloatingBox>
  )
}

type ICustomer = IRecord<
  "customers",
  | "name"
  | "account_reference"
  | "account_on_hold"
  | "outstanding_balance_pennies"
  | "credit_limit_pennies"
  | "tax_rate"
  | "memo"
  | "delivery_note_email"
  | "internalCustomer"
  | "analysisCodes"
  | "currency_name"
  | "cash_sales"
  | "defaultDeliveryMethod"
> & {
  defaultDeliveryService?: IRecord<"products", "body" | "sale_price_pennies">
  addresses: IRecord<"addresses", "postcode" | "importer_id">[]
}

interface ICustomerSelectProps {
  label?: string
  customer?: ICustomer
  error?: string
  onChange: (data: { customer?: ICustomer; address?: IAddress; shippingAddress?: IAddress }) => void
  quote?: IQuoteOrderRecord
  isClearable?: boolean
  isQuote?: boolean
  disableOnHoldCustomers?: boolean
  postFieldText?: React.ReactNode
  disabled?: boolean
  tag?: IComponentProp<typeof CustomerTagFilter, "tag">
  tagValue?: IComponentProp<typeof CustomerTagFilter, "value">
}

export function CustomerSelect({
  label = "Select Customer",
  customer,
  error,
  onChange,
  quote,
  isClearable,
  isQuote,
  disableOnHoldCustomers,
  postFieldText,
  disabled,
  tag,
  tagValue,
}: ICustomerSelectProps) {
  const onHoldCustomerDisableSelect = disableOnHoldCustomers && !isQuote
  const skeem = window.skeem
  const { currentUser } = useGlobalState()

  async function handleLoadCustomers(query: string) {
    if (query.length < 3) {
      return []
    }
    const filter: { and: any[] } = {
      and: [
        {
          matches: { branch: currentUser.branch.id },
        },
      ].filter(Boolean),
    }

    if (query) {
      filter.and.push({
        or: [
          {
            matches: {
              name: { like: query },
            },
          },
          {
            matches: {
              account_reference: { like: query },
            },
          },
          {
            exists: {
              attr: {
                name: "addresses",
                filter: { matches: { postcode: { like: query } } },
              },
            },
          },
        ],
      })
    }

    if (tagValue) {
      filter.and.push({
        matches: {
          tag_values2: { some: { id: tagValue.id } },
        },
      })
    } else if (tag) {
      filter.and.push({
        matches: {
          tag_values2: { some: { tag: tag.id } },
        },
      })
    }

    const customers = await skeem.fetch2<ICustomer[]>({
      model: {
        name: "customers",
        attributes: [
          "name",
          "account_reference",
          "account_on_hold",
          "outstanding_balance_pennies",
          "credit_limit_pennies",
          "tax_rate",
          "memo",
          "internalCustomer",
          "analysisCodes",
          "cash_sales",
          "defaultDeliveryMethod",
          { name: "defaultDeliveryService", attributes: ["body", "sale_price_pennies"] },
          {
            name: "addresses",
            attributes: ["postcode", "importer_id"],
            filter: { not: { matches: { importer_id: "" } } },
            paginate: { page: 1, size: 2 },
            sort: {
              by: "isDefault",
              direction: "DESC",
            },
          },
        ],
        filter: filter,
        paginate: { page: 1, size: 20 },
      },
    })

    return customers
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
      .sort(
        (a, b) =>
          a.name.toLowerCase().indexOf(query.toLowerCase()) -
          b.name.toLowerCase().indexOf(query.toLowerCase()),
      )
  }

  async function handleChangeCustomer(selected: { id: string }) {
    if (!selected?.id) {
      onChange({ customer: undefined })
      return
    }
    const customers = await skeem.fetch<
      ICustomer & {
        addresses: IAddress[]
        shippingAddresses: IAddress[]
        delivery_note_contacts: IRecord<"contacts", "email">[]
        default_contacts: IRecord<"contacts", "email">[]
      }
    >({
      customers: {
        attributes: [
          { name: "defaultDeliveryService", attributes: ["body", "sale_price_pennies"] },
          "defaultDeliveryMethod",
          "name",
          "account_reference",
          "account_on_hold",
          "outstanding_balance_pennies",
          "credit_limit_pennies",
          "tax_rate",
          "memo",
          "delivery_note_email",
          "publishQuotesByDefault",
          "internalCustomer",
          "analysisCodes",
          "currency_name",
          "cash_sales",
          {
            as: "delivery_note_contacts",
            value: {
              attr: {
                name: "contacts",
                attributes: ["email"],
                filter: { matches: { default_delivery_note: true } },
              },
            },
          },
          {
            as: "default_contacts",
            value: {
              attr: {
                name: "contacts",
                attributes: ["email"],
                filter: { matches: { is_default: true } },
              },
            },
          },
          {
            name: "addresses",
            attributes: [
              "label",
              "name",
              "email",
              "phone",
              "line_1",
              "line_2",
              "line_3",
              "line_4",
              "city",
              "county",
              "country",
              "postcode",
              "contact",
            ],
            filter: {
              and: [
                { not: { matches: { importer_id: "" } } },
                { not: { matches: { line_1: "" } } },
              ],
            },
            paginate: { page: 1, size: 2 },
            sort: {
              by: "isDefault",
              direction: "DESC",
            },
          },
          {
            as: "shippingAddresses",
            value: {
              attr: {
                name: "addresses",
                attributes: [
                  "label",
                  "name",
                  "email",
                  "phone",
                  "line_1",
                  "line_2",
                  "line_3",
                  "line_4",
                  "city",
                  "county",
                  "country",
                  "postcode",
                  "contact",
                ],
                paginate: { page: 1, size: 2 },
                filter: { matches: { isDefaultShipping: true } },
                sort: {
                  by: "isDefault",
                  direction: "DESC",
                },
              },
            },
          },
        ],
        filter: { matches: { id: [customer?.id, selected?.id] } },
      },
    })

    const newCustomer = customers.find(c => c.id === selected?.id)!

    const dataPartial: {
      delivery_note_email?: string
      contact?: { id: string; email: string }
      currency_name?: string
    } = {
      currency_name:
        getCurrencyOption(newCustomer.currency_name)?.value ??
        getCurrencyOption("pound sterling")?.value,
    }
    if (!isQuote) {
      if (newCustomer.delivery_note_contacts.length > 0) {
        dataPartial.delivery_note_email = newCustomer.delivery_note_contacts[0].email
      } else {
        dataPartial.delivery_note_email = newCustomer.delivery_note_email
      }
    }
    if (newCustomer.default_contacts.length === 1) {
      dataPartial.contact = newCustomer.default_contacts[0]
    }
    const address = newCustomer.addresses[0]
    const shippingAddress = newCustomer.shippingAddresses[0]

    onChange({ ...dataPartial, customer: newCustomer, address: address, shippingAddress })
  }

  const hasJob = !!quote?.job?.id

  return (
    <div>
      <Fields.AsyncSelect
        label={label}
        value={customer}
        loadOptions={handleLoadCustomers}
        onChange={handleChangeCustomer}
        getOptionLabel={opt => opt.name ?? ""}
        getOptionValue={opt => opt.id}
        error={error}
        isClearable={isClearable}
        disabled={hasJob || disabled}
        isOptionDisabled={onHoldCustomerDisableSelect ? opt => opt.account_on_hold : undefined}
        postFieldText={postFieldText}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 3 ? "Type 3 characters to search..." : "No results"
        }
        components={{
          SingleValue: props => {
            const { innerProps, getStyles } = props

            return (
              <div
                {...innerProps}
                style={{
                  ...getStyles("singleValue", props),
                }}
              >
                <div>{props.data.name}</div>
                {props.data.addresses?.[0] && (
                  <div
                    css={css`
                      color: #999;
                      font-size: 10px;
                      font-weight: 500;
                    `}
                  >
                    {props.data.addresses[0].postcode}
                  </div>
                )}
              </div>
            )
          },
          Option: props => {
            const { innerProps, innerRef, getStyles } = props

            return (
              <div
                ref={innerRef}
                {...innerProps}
                style={{
                  ...getStyles("option", props),
                  backgroundColor: props.data.account_on_hold
                    ? themeColors.lightRed
                    : (getStyles("option", props) as any).backgroundColor,
                  color:
                    props.data.account_on_hold && disableOnHoldCustomers
                      ? "black"
                      : (getStyles("option", props) as any).color,
                  cursor:
                    props.data.account_on_hold && disableOnHoldCustomers
                      ? "not-allowed"
                      : (getStyles("option", props) as any).cursor,
                }}
              >
                <div>
                  {props.data.name}
                  {props.data.account_on_hold ? (
                    <span>
                      {" "}
                      <b>(ON HOLD)</b>
                    </span>
                  ) : undefined}
                </div>
                {props.data.addresses[0] && (
                  <div
                    css={css`
                      color: #999;
                      font-size: 10px;
                      font-weight: 500;
                    `}
                  >
                    {props.data.addresses[0].postcode}
                  </div>
                )}
              </div>
            )
          },
        }}
      />
      {hasJob && (
        <React.Fragment>
          <Spacer size={3} />
          <P size="small" color="#888">
            <Icon icon={icons.info} /> Can't change customer when building{" "}
            {isQuote ? "a quote" : "an order"} for{" "}
            {isQuote ? "a pricing request" : "an order request"}
          </P>
        </React.Fragment>
      )}
    </div>
  )
}

function useCreateSubCustomer() {
  const skeem = window.skeem
  return useConfirmation<
    {
      customer: IRecord<"customers", "name">
    },
    null | IRecord<"sub_customers", "name">
  >(({ data, onResolve }) => {
    async function handleSubmit(formData: { name: string }) {
      const subCustomer = await skeem.mutate<IRecord<"sub_customers", "name">>({
        sub_customers: { create: { name: formData.name, customer: { set: data.customer.id } } },
      })
      onResolve(subCustomer)
    }

    return (
      <Modal
        render={() => (
          <Grid gap={12}>
            <TextGroupMedium
              heading={"Create sub customer"}
              body={`Sub customer for customer: ${data.customer.name}`}
            />
            <Form2.Form onSubmit={handleSubmit}>
              <Form2.Text field="name" validations={{ presence: true }} />
              <Grid columns={["auto", "max-content"]}>
                <Form2.WithForm
                  render={form => (
                    <ConfirmCancelButtons
                      onConfirm={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        form.submit()
                      }}
                      onCancel={() => onResolve(null)}
                    />
                  )}
                />
              </Grid>
            </Form2.Form>
          </Grid>
        )}
        isOpen
      />
    )
  })
}
